import React, { useCallback, useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  IconButton, 
  Avatar, 
  Paper,
  CircularProgress,
  Stack
} from '@mui/material';
import { 
  PhotoCamera as PhotoCameraIcon,
  Delete as DeleteIcon 
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import imageCompression from 'browser-image-compression';
import { PhotoUploadError } from './types';
import { validateImage } from './utils';

interface PhotoUploadProps {
  value?: string | File;
  onChange: (file: File | null) => void;
  onError: (error: PhotoUploadError | null) => void;
  maxSize?: number; // in bytes
  previewSize?: number;
}

export const PhotoUpload: React.FC<PhotoUploadProps> = ({
  value,
  onChange,
  onError,
  maxSize = 5 * 1024 * 1024, // 5MB default
  previewSize = 120
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [preview, setPreview] = useState<string | undefined>(
    typeof value === 'string' ? value : undefined
  );

  // Update preview when value changes
  useEffect(() => {
    if (typeof value === 'string') {
      setPreview(value);
    }
  }, [value]);

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (preview && preview.startsWith('blob:')) {
        URL.revokeObjectURL(preview);
      }
    };
  }, [preview]);

  const handleCompressImage = async (file: File): Promise<File> => {
    const options = {
      maxSizeMB: maxSize / (1024 * 1024),
      maxWidthOrHeight: 800,
      useWebWorker: true
    };

    try {
      return await imageCompression(file, options);
    } catch (error) {
      throw new Error('Image compression failed');
    }
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];
    const validationError = validateImage(file, maxSize);

    if (validationError) {
      onError(validationError);
      return;
    }

    try {
      setIsLoading(true);
      onError(null);

      // Cleanup old preview if it's a blob URL
      if (preview && preview.startsWith('blob:')) {
        URL.revokeObjectURL(preview);
      }

      // Compress image if needed
      const processedFile = file.size > maxSize 
        ? await handleCompressImage(file)
        : file;

      // Create preview URL
      const previewUrl = URL.createObjectURL(processedFile);
      setPreview(previewUrl);
      onChange(processedFile);

    } catch (error) {
      onError({
        code: 'compression_failed',
        message: 'Failed to process image. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  }, [maxSize, onChange, onError, preview]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/webp': ['.webp']
    },
    maxFiles: 1,
    multiple: false
  });

  const handleDelete = () => {
    if (preview && preview.startsWith('blob:')) {
      URL.revokeObjectURL(preview);
    }
    setPreview(undefined);
    onChange(null);
    onError(null);
  };

  return (
    <Stack spacing={2} alignItems="center">
      {preview ? (
        <Box sx={{ position: 'relative' }}>
          <Avatar
            src={preview}
            sx={{
              width: previewSize,
              height: previewSize,
              border: 2,
              borderColor: 'background.paper',
              boxShadow: 1
            }}
          />
          <IconButton
            sx={{
              position: 'absolute',
              right: -8,
              bottom: -8,
              bgcolor: 'background.paper',
              boxShadow: 1,
              '&:hover': {
                bgcolor: 'error.light',
                color: 'error.contrastText'
              }
            }}
            onClick={handleDelete}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ) : (
        <Paper
          {...getRootProps()}
          sx={{
            p: 3,
            border: '2px dashed',
            borderColor: isDragActive ? 'primary.main' : 'divider',
            borderRadius: 2,
            bgcolor: isDragActive ? 'primary.lighter' : 'background.paper',
            cursor: 'pointer',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2
          }}
        >
          <input {...getInputProps()} />
          {isLoading ? (
            <CircularProgress size={40} />
          ) : (
            <>
              <PhotoCameraIcon color="action" sx={{ fontSize: 40 }} />
              <Typography variant="body2" color="text.secondary" align="center">
                {isDragActive
                  ? 'Drop the image here'
                  : 'Drag & drop an image here, or click to select'}
              </Typography>
              <Typography variant="caption" color="text.secondary" align="center">
                Supported formats: JPG, PNG, WebP (max {maxSize / (1024 * 1024)}MB)
              </Typography>
            </>
          )}
        </Paper>
      )}
    </Stack>
  );
};